import { UAParser } from 'ua-parser-js';
import { logger } from '../utils/logger';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Custom dimensions indices
const CUSTOM_DIMENSIONS = {
  DEVICE_TYPE: 'dimension1',
  BROWSER: 'dimension2',
  OS: 'dimension3',
  USER_TYPE: 'dimension4',
  QUESTION_CATEGORY: 'dimension5',
};

// Event names
const EVENTS = {
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
  USER_ACTION: 'user_action',
  QUESTION_ASKED: 'question_asked',
  RESPONSE_RECEIVED: 'response_received',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  ERROR_OCCURRED: 'error_occurred',
  PERFORMANCE_METRIC: 'performance_metric',
};

class AnalyticsService {
  private deviceInfo: UAParser.IResult;
  private initialized = false;

  constructor() {
    this.deviceInfo = new UAParser().getResult();
  }

  init(measurementId: string) {
    if (this.initialized) return;

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());

    // Configure with custom dimensions
    window.gtag('config', measurementId, {
      custom_map: {
        [CUSTOM_DIMENSIONS.DEVICE_TYPE]: 'device_type',
        [CUSTOM_DIMENSIONS.BROWSER]: 'browser',
        [CUSTOM_DIMENSIONS.OS]: 'os',
        [CUSTOM_DIMENSIONS.USER_TYPE]: 'user_type',
        [CUSTOM_DIMENSIONS.QUESTION_CATEGORY]: 'question_category',
      },
      ...this.getDeviceInfo(),
    });

    this.initialized = true;
    logger.debug('Analytics initialized');
  }

  private getDeviceInfo() {
    return {
      device_type: this.deviceInfo.device.type || 'desktop',
      browser: this.deviceInfo.browser.name || 'unknown',
      os: this.deviceInfo.os.name || 'unknown',
    };
  }

  private sendEvent(eventName: string, params: Record<string, any> = {}) {
    if (!this.initialized) {
      logger.warn('Analytics not initialized');
      return;
    }

    try {
      window.gtag('event', eventName, {
        ...params,
        ...this.getDeviceInfo(),
      });
    } catch (error) {
      logger.error('Failed to send analytics event', {
        error,
        eventName,
        params,
      });
    }
  }

  // Session Tracking
  trackSessionStart() {
    this.sendEvent(EVENTS.SESSION_START, {
      timestamp: new Date().toISOString(),
    });
  }

  trackSessionEnd(duration: number, interactions: number) {
    this.sendEvent(EVENTS.SESSION_END, {
      duration,
      interactions,
    });
  }

  // User Activity Tracking
  trackUserAction(action: string, details?: Record<string, any>) {
    this.sendEvent(EVENTS.USER_ACTION, {
      action_type: action,
      ...details,
    });
  }

  // Question Tracking
  trackQuestion(question: string, category: string) {
    this.sendEvent(EVENTS.QUESTION_ASKED, {
      question,
      [CUSTOM_DIMENSIONS.QUESTION_CATEGORY]: category,
    });
  }

  trackResponse(questionId: string, responseTime: number, successful: boolean) {
    this.sendEvent(EVENTS.RESPONSE_RECEIVED, {
      question_id: questionId,
      response_time: responseTime,
      success: successful,
    });
  }

  // Feedback Tracking
  trackFeedback(questionId: string, rating: number) {
    this.sendEvent(EVENTS.FEEDBACK_SUBMITTED, {
      question_id: questionId,
      rating,
    });
  }

  // Error Tracking
  trackError(error: Error, context?: Record<string, any>) {
    this.sendEvent(EVENTS.ERROR_OCCURRED, {
      error_name: error.name,
      error_message: error.message,
      ...context,
    });
  }

  // Performance Tracking
  trackPerformance(
    metric: string,
    value: number,
    context?: Record<string, any>
  ) {
    this.sendEvent(EVENTS.PERFORMANCE_METRIC, {
      metric_name: metric,
      metric_value: value,
      ...context,
    });
  }

  // User Properties
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) return;

    window.gtag('set', 'user_properties', properties);
  }

  // Page Views (in addition to automatic tracking)
  trackPageView(path: string, title: string) {
    this.sendEvent('page_view', {
      page_path: path,
      page_title: title,
    });
  }
}

export const analyticsService = new AnalyticsService();
